import { FC } from 'react';

import { Formik } from 'formik';
import { isStrictEqual } from 'remeda';
import { BaseInput, FormBodyContainer, BaseAutocomplete, BaseSelect, BaseCheckbox } from 'wbs-components-lib';

import { VipTaskMarksEnum, VipTaskTypesEnum } from '@shared/constants/vip';
import { prepareItemsForBaseSelect } from '@shared/utils';

import { VipTaskFormProps } from './types';
import { vipTaskValidationSchema } from './validationSchema';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';

export const VipTaskForm: FC<VipTaskFormProps> = ({
  initialValues,
  onSubmit,
  isSubmitting = false,
  submitButtonCaption = 'Create',
}) => {
  const selectTaskTypeItems = prepareItemsForBaseSelect(Object.values(VipTaskTypesEnum));

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={vipTaskValidationSchema}
      enableReinitialize
    >
      {({ values, handleChange, handleBlur, setFieldValue, submitForm, errors, touched }) => {
        const {
          title: titleValue,
          imageId: imageIdValue,
          link: linkValue,
          promoCode: promoCodeValue,
          ymId: ymIdValue,
          casinoName: casinoNameValue,
          type: typeValue,
          marks: marksValue,
          isActive: isActiveValue,
        } = values;
        const {
          title: titleError,
          link: linkError,
          promoCode: promoCodeError,
          ymId: ymIdError,
          casinoName: casinoNameError,
          type: typeError,
          marks: marksError,
        } = errors;
        const {
          title: isTitleTouched,
          link: isLinkTouched,
          promoCode: isPromoCodeTouched,
          ymId: isYmIdTouched,
          casinoName: isCasinoNameTouched,
          type: isTypeTouched,
          marks: isMarksTouched,
        } = touched;

        const isTitleError = isTitleTouched && !!titleError;
        const isLinkError = isLinkTouched && !!linkError;
        const isPromoCodeError = isPromoCodeTouched && !!promoCodeError;
        const isYmIdError = isYmIdTouched && !!ymIdError;
        const isCasinoNameError = isCasinoNameTouched && !!casinoNameError;
        const isTypeError = isTypeTouched && !!typeError;
        const isMarksError = isMarksTouched && !!marksError;

        const isSubmitButtonDisabled = isStrictEqual(values, initialValues);

        return (
          <FormBodyContainer
            isSubmitting={isSubmitting}
            submitButtonCaption={submitButtonCaption}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
            onSubmit={submitForm}
          >
            <BaseInput
              helperText={isTitleError && titleError}
              label="Enter title"
              id="title"
              name="title"
              caption="Title"
              value={titleValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={isTitleError}
            />
            <FileSelector
              label="Image"
              value={imageIdValue}
              onSelect={(value) => setFieldValue('imageId', value)}
              required
            />
            <BaseSelect
              // @ts-ignore
              id="type"
              name="type"
              errorText={typeError}
              isError={isTypeError}
              onBlur={handleBlur}
              items={selectTaskTypeItems}
              handleChange={(value: string) => setFieldValue('type', value)}
              value={typeValue || VipTaskTypesEnum.PLATFORM}
              caption="Task Type"
              label="Select task type"
            />
            {typeValue === VipTaskTypesEnum.PARTNER && (
              <>
                <BaseInput
                  helperText={isPromoCodeError && promoCodeError}
                  label="Enter promo code"
                  id="promoCode"
                  name="promoCode"
                  caption="Promo code"
                  value={promoCodeValue}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={isPromoCodeError}
                />
                <BaseInput
                  helperText={isLinkError && linkError}
                  label="Enter link"
                  id="link"
                  name="link"
                  caption="Link"
                  value={linkValue}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={isLinkError}
                />
                <BaseInput
                  helperText={isYmIdError && ymIdError}
                  label="Enter ymId"
                  id="ymId"
                  name="ymId"
                  caption="YmId"
                  value={ymIdValue}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={isYmIdError}
                />
                <BaseInput
                  helperText={isCasinoNameError && casinoNameError}
                  label="Enter casino name"
                  id="casinoName"
                  name="casinoName"
                  caption="Casino name"
                  value={casinoNameValue}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={isCasinoNameError}
                />
              </>
            )}
            <BaseAutocomplete
              handleChange={(_, newValue: VipTaskMarksEnum[]) => setFieldValue('marks', newValue)}
              caption="Marks"
              handleBlur={handleBlur}
              id="marks"
              isError={isMarksError}
              name="marks"
              label="Select marks"
              options={Object.values(VipTaskMarksEnum)}
              value={marksValue}
              helperText={isMarksError && marksError}
            />
            <BaseCheckbox
              id="isActive"
              name="isActive"
              title="Active"
              checked={isActiveValue}
              onChange={handleChange}
            />
          </FormBodyContainer>
        );
      }}
    </Formik>
  );
};
